import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import { authSessionUserSchema, type AuthSessionUser } from '@aia/platform-core';

import { api, authApi, getApiError, isApiError } from './api';
import { setAuthenticatedUser, clearAuthenticatedUser } from './telemetry';

export type User = AuthSessionUser;

export interface LoginResult {
  success: boolean;
  error?: string;
  /** True when the password was correct but a second factor is required. */
  mfaRequired?: boolean;
  /** Short-lived token to pass to verifyMfa when mfaRequired is true. */
  mfaToken?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True when the signed-in user must enrol in MFA before using the app. */
  mfaEnrollmentRequired: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<LoginResult>;
  /** Completes an MFA-gated login with a TOTP code or a backup code. */
  verifyMfa: (
    mfaToken: string,
    opts: { code?: string; backupCode?: string },
  ) => Promise<{ success: boolean; error?: string }>;
  /** Called after successful enrolment to clear the enrolment gate. */
  markMfaEnrolled: () => void;
  logout: () => void;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; error?: string }>;
  setAuth: (token: string, user: User) => void;
}

export function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (!parts[1]) return true;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    return (payload.exp ?? 0) * 1000 < Date.now();
  } catch {
    return true;
  }
}

const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);

  useEffect(() => {
    const storedToken = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    const storedUser = sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      sessionStorage.setItem(TOKEN_KEY, storedToken);
      sessionStorage.setItem(USER_KEY, storedUser);
      setToken(storedToken);
      try {
        const parsed: unknown = JSON.parse(storedUser) as unknown;
        const result = authSessionUserSchema.safeParse(parsed);
        if (!result.success) {
          clearAuth();
          setIsLoading(false);
          return;
        }
        setUser(result.data as User);
        // Show protected UI immediately from the stored session. /auth/me
        // revalidation continues in the background; a failed verify still
        // clears the session via clearAuth().
        setIsLoading(false);
      } catch {
        clearAuth();
        setIsLoading(false);
        return;
      }
      void verifyToken();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async () => {
    try {
      const response = await authApi.get<User>('/auth/me');
      const verifiedUser = response.data;
      if (!verifiedUser.isPlatformAdmin) {
        clearAuth();
        return;
      }
      setUser(verifiedUser);
      sessionStorage.setItem(USER_KEY, JSON.stringify(verifiedUser));
    } catch (err) {
      // Only drop the session on definitive auth failures. Transient API
      // errors should not blank the Admin shell mid-navigation.
      if (isApiError(err) && (err.status === 401 || err.status === 403)) {
        clearAuth();
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkExpiry = () => {
      const t = sessionStorage.getItem(TOKEN_KEY);
      if (t && isTokenExpired(t)) {
        clearAuth();
      }
    };

    const interval = setInterval(checkExpiry, 60_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAuth = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    setMfaEnrollmentRequired(false);
    clearAuthenticatedUser();
  }, []);

  const persistSession = useCallback((newUser: User, newToken: string) => {
    sessionStorage.setItem(TOKEN_KEY, newToken);
    sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
    setAuthenticatedUser(newUser.id, newUser.organizationId ?? undefined);
  }, []);

  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string): Promise<LoginResult> => {
      try {
        // Staging / ?e2e=true bypass tokens — always attach the header so login
        // does not depend solely on sessionStorage timing.
        const e2eHeaders =
          turnstileToken === 'e2e-test-bypass' || turnstileToken === 'turnstile-disabled'
            ? { 'X-E2E-Bypass': 'true' }
            : undefined;
        const response = await authApi.post<{
          user: User;
          token: string;
          mfaEnrollmentRequired?: boolean;
        }>('/auth/login', { email, password, turnstileToken }, { headers: e2eHeaders });

        const newUser = response.data.user;
        const newToken = response.data.token;

        if (!newUser.isPlatformAdmin) {
          return {
            success: false,
            error: 'Access denied. This portal is for platform administrators only.',
          };
        }

        persistSession(newUser, newToken);
        setMfaEnrollmentRequired(!!response.data.mfaEnrollmentRequired);

        return { success: true };
      } catch (err) {
        // A correct password with MFA enabled returns 401 + an MFA challenge.
        if (isApiError(err) && err.status === 401) {
          const body = err.response as { error?: { code?: string }; mfaToken?: string } | undefined;
          if (body?.error?.code === 'MFA_REQUIRED' && body.mfaToken) {
            return { success: false, mfaRequired: true, mfaToken: body.mfaToken };
          }
        }
        return { success: false, error: getApiError(err) };
      }
    },
    [persistSession],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, opts: { code?: string; backupCode?: string }) => {
      try {
        const response = await authApi.post<{ user: User; token: string }>('/auth/mfa/verify', {
          mfaToken,
          ...opts,
        });

        const newUser = response.data.user;
        const newToken = response.data.token;

        if (!newUser.isPlatformAdmin) {
          return {
            success: false,
            error: 'Access denied. This portal is for platform administrators only.',
          };
        }

        persistSession(newUser, newToken);
        setMfaEnrollmentRequired(false);
        return { success: true };
      } catch (err) {
        return { success: false, error: getApiError(err) };
      }
    },
    [persistSession],
  );

  const markMfaEnrolled = useCallback(() => setMfaEnrollmentRequired(false), []);

  const logout = useCallback(() => {
    void api.post('/auth/logout').catch(() => {
      // logout error is non-critical
    });
    clearAuth();
  }, [clearAuth]);

  const forgotPassword = useCallback(async (email: string) => {
    try {
      await authApi.post('/auth/forgot-password', { email });
      return { success: true };
    } catch (err) {
      return { success: false, error: getApiError(err) };
    }
  }, []);

  const resetPassword = useCallback(async (resetToken: string, password: string) => {
    try {
      await authApi.post('/auth/reset-password', { token: resetToken, password });
      return { success: true };
    } catch (err) {
      return { success: false, error: getApiError(err) };
    }
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    mfaEnrollmentRequired,
    login,
    verifyMfa,
    markMfaEnrolled,
    logout,
    forgotPassword,
    resetPassword,
    setAuth: (newToken: string, newUser: User) => persistSession(newUser, newToken),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
